import {
  CueErrorPayload,
  CueSendResult,
  CueSyncActivity,
  CueUserGuidance,
  CueUserWarning,
  CueValidationError,
} from 'src/app/models/fincas/cue.model';

interface CueGuidanceRule {
  readonly action: string;
  readonly location: string;
  readonly message: string;
  readonly patterns: readonly string[];
}

/** Converts CUE validation and SIEX errors into actionable user messages. */
export class CueErrorPresenter {
  private static readonly GUIDANCE_RULES: readonly CueGuidanceRule[] = [
    {
      action: 'Completa el ROPO del aplicador y vuelve a enviar las actividades corregidas.',
      location: 'Tratamientos > Aplicador > Número ROPO',
      message: 'Falta el número ROPO del aplicador.',
      patterns: ['ropo'],
    },
    {
      action: 'Revisa el código ROMA de la máquina y vuelve a enviar las actividades corregidas.',
      location: 'Recursos > Maquinaria > Registro ROMA',
      message: 'La máquina no tiene un registro ROMA válido.',
      patterns: ['roma'],
    },
    {
      action: 'Revisa la parcela indicada y vuelve a enviar las actividades corregidas.',
      location: 'Finca > Parcelas > Datos SIGPAC',
      message: 'La parcela no tiene una referencia SIGPAC válida.',
      patterns: ['sigpac', 'recinto'],
    },
    {
      action: 'Revisa la parcela indicada y vuelve a enviar las actividades corregidas.',
      location: 'Finca > Parcelas > Datos REA/DGC',
      message: 'La parcela no tiene un código DGC válido.',
      patterns: ['dgc'],
    },
    {
      action: 'Corrige la dosis aplicada y vuelve a enviar las actividades corregidas.',
      location: 'Actividad > Dosis',
      message: 'La dosis indicada no es válida.',
      patterns: ['dosis', 'dosificación', 'dosificacion'],
    },
    {
      action: 'Corrige la cantidad aplicada y vuelve a enviar las actividades corregidas.',
      location: 'Actividad > Cantidad',
      message: 'La cantidad indicada no es válida.',
      patterns: ['cantidad', 'quantitat', 'volumen', 'volum'],
    },
    {
      action: 'Revisa la superficie de la parcela o de la actividad y vuelve a enviar las actividades corregidas.',
      location: 'Finca > Parcelas > Superficie',
      message: 'La superficie indicada no es válida.',
      patterns: ['superficie', 'superfície', 'hectárea', 'hectarea'],
    },
    {
      action: 'Selecciona un cultivo activo para la parcela y vuelve a enviar las actividades corregidas.',
      location: 'Finca > Parcelas > Cultivos',
      message: 'La actividad necesita un cultivo válido.',
      patterns: ['cultivo', 'cultiu'],
    },
    {
      action: 'Revisa la variedad del cultivo y vuelve a enviar las actividades corregidas.',
      location: 'Finca > Parcelas > Cultivos > Variedad',
      message: 'La variedad indicada no es válida.',
      patterns: ['variedad', 'varietat'],
    },
    {
      action: 'Revisa el fertilizante o los datos de fertilización y vuelve a enviar las actividades corregidas.',
      location: 'Fertilización > Producto y dosis',
      message: 'La fertilización necesita revisión.',
      patterns: ['fertilizante', 'fertilización', 'fertilizacion', 'abonado', 'abono'],
    },
    {
      action: 'Revisa los datos de riego o fertirrigación y vuelve a enviar las actividades corregidas.',
      location: 'Riego > Datos del riego',
      message: 'Los datos de riego necesitan revisión.',
      patterns: ['riego', 'fertirrigación', 'fertirrigacion', 'fertirriego'],
    },
    {
      action: 'Corrige la fecha de la actividad y vuelve a enviar las actividades corregidas.',
      location: 'Actividad > Fecha',
      message: 'La fecha de la actividad no es válida.',
      patterns: ['fecha', 'data', 'campaña', 'campana', 'futura'],
    },
    {
      action: 'Selecciona un tipo de maquinaria válido y vuelve a enviar las actividades corregidas.',
      location: 'Tratamiento > Equipo aplicador > Tipo de maquinaria',
      message: 'La maquinaria no tiene un código UNE válido.',
      patterns: ['tipomaquinariaune', 'maquinaria', 'une'],
    },
    {
      action: 'Revisa el producto indicado y vuelve a enviar las actividades corregidas.',
      location: 'Tratamientos > Producto aplicado',
      message: 'El producto fitosanitario necesita revisión.',
      patterns: [
        'producto fitosanitario',
        'fitosanitario',
        'registro fitosanitario',
        'nº registro',
        'numregistro',
      ],
    },
  ];

  public static formatErrors(payload?: CueErrorPayload | CueSendResult): string {
    const issues = CueErrorPresenter.issuesFromPayload(payload);

    if (issues.length > 0) {
      return issues.map((item) => CueErrorPresenter.formatIssue(item)).join('\n\n');
    }

    return 'No hemos podido completar el envío. Revisa los avisos o contacta con soporte si el problema continúa.';
  }

  public static formatDetalleImportacion(detail: string | Array<{ codigo?: string; descripcion?: string }> | undefined): string {
    if (Array.isArray(detail) && detail.length > 0) {
      return 'La administración ha indicado uno o más problemas en el envío. Revisa el detalle técnico si soporte te lo pide.';
    }

    if (typeof detail === 'string' && detail.trim().length > 0) {
      return 'La administración ha indicado un problema en el envío. Revisa el detalle técnico si soporte te lo pide.';
    }

    return 'La administración ha indicado un problema en el envío. Revisa el detalle técnico si soporte te lo pide.';
  }
  
  public static formatRejectedActivity(activity: CueSyncActivity): string {
    return CueErrorPresenter.formatIssue(CueErrorPresenter.warningFromRejectedActivity(activity, 0));
  }

  public static formatValidationError(error: CueValidationError): string {
    return CueErrorPresenter.formatIssue(CueErrorPresenter.warningFromValidationError(error, 0));
  }

  public static warningsFromPayload(payload?: CueErrorPayload | CueSendResult): CueUserWarning[] {
    return CueErrorPresenter.issuesFromPayload(payload);
  }

  public static warningsFromRejectedActivities(activities: CueSyncActivity[]): CueUserWarning[] {
    return CueErrorPresenter.issuesFromRejectedActivities(activities);
  }

  public static issuesFromPayload(payload?: CueErrorPayload | CueSendResult): CueUserWarning[] {
    return CueErrorPresenter.extractValidationErrors(payload)
      .map((error, index) => CueErrorPresenter.warningFromValidationError(error, index));
  }

  public static issuesFromRejectedActivities(activities: CueSyncActivity[]): CueUserWarning[] {
    return activities
      .filter((activity) => activity.es_valida === false)
      .map((activity, index) => CueErrorPresenter.warningFromRejectedActivity(activity, index));
  }

  public static warningFromRejectedActivity(activity: CueSyncActivity, index: number): CueUserWarning {
    const message = activity.remote_error_message ?? 'La administración no ha devuelto un detalle concreto.';
    const guidance = CueErrorPresenter.buildUserGuidance(message);

    return {
      action: guidance.action,
      id: 'rejected-' + (activity.activity_type ?? 'activity') + '-' + (activity.local_activity_id ?? index),
      location: guidance.location,
      message: guidance.message,
      severity: 'warn',
      technicalDetail: CueErrorPresenter.rejectedActivityTechnicalDetail(activity),
      title: CueErrorPresenter.activityUserLabel(activity)
    };
  }

  public static warningFromValidationError(error: CueValidationError, index: number): CueUserWarning {
    const rawMessage = error.descripcion ?? error.message ?? error.motive ?? 'Revisa los datos antes de enviar.';
    const guidance = CueErrorPresenter.buildUserGuidance(rawMessage, error.property);

    return {
      action: guidance.action,
      id: 'validation-' + (error.activity_type ?? 'activity') + '-' + (error.local_activity_id ?? error.row_index ?? index),
      location: guidance.location,
      message: guidance.message,
      severity: 'warn',
      technicalDetail: CueErrorPresenter.validationErrorTechnicalDetail(error),
      title: CueErrorPresenter.validationErrorTitle(error)
    };
  }

  public static activityUserLabel(activity: CueSyncActivity): string {
    const activityType = String(activity.activity_type ?? '').toLowerCase();
    const localId = activity.local_activity_id ? ' #' + activity.local_activity_id : '';

    if (activityType === 'fito' || activityType === 'tratamfito') {
      return 'Tratamiento fitosanitario' + localId;
    }

    if (activityType === 'ferti' || activityType === 'fertilizacion') {
      return 'Fertilización' + localId;
    }

    return 'Actividad' + localId;
  }

  public static buildUserGuidance(message: string, property?: string): CueUserGuidance {
    const combinedText = CueErrorPresenter.normalizeSearchText(message + ' ' + (property ?? ''));
    const matchingRule = CueErrorPresenter.GUIDANCE_RULES.find((rule) =>
      rule.patterns.some((pattern) =>
        combinedText.includes(CueErrorPresenter.normalizeSearchText(pattern))
      )
    );

    if (matchingRule) {
      return {
        action: matchingRule.action,
        location: matchingRule.location,
        message: matchingRule.message
      };
    }

    return {
      action: 'Corrige el dato indicado y vuelve a enviar las actividades corregidas.',
      location: 'Datos de la actividad indicada',
      message: 'Hay un dato de la actividad que la administración no ha podido validar.'
    };
  }


  private static normalizeSearchText(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  private static formatIssue(issue: CueUserWarning): string {
    const lines = [
      'Actividad con problema: ' + issue.title,
      'Motivo: ' + issue.message,
      'Dónde corregirlo: ' + issue.location,
      'Acción: ' + issue.action
    ];

    return lines.join('\n');
  }

  private static extractValidationErrors(payload?: CueErrorPayload | CueSendResult): CueValidationError[] {
    return payload?.errors ?? payload?.local_exception?.errors ?? (payload as CueErrorPayload | undefined)?.data?.errors ?? [];
  }

  private static rejectedActivityTechnicalDetail(activity: CueSyncActivity): string {
    const lines = [
      'Tipo actividad: ' + (activity.activity_type ?? '-'),
      'ID local: ' + (activity.local_activity_id ?? '-'),
      'ID ajena: ' + (activity.id_ajena ?? '-'),
      'Código remoto: ' + (activity.remote_error_code ?? '-'),
      'Mensaje remoto: ' + (activity.remote_error_message ?? '-'),
      'IdCuaderno: ' + (activity.id_cuaderno ?? '-')
    ];

    return lines.join('\n');
  }

  private static validationErrorTechnicalDetail(error: CueValidationError): string {
    const lines = [
      'Tipo actividad: ' + (error.activity_type ?? '-'),
      'ID local: ' + (error.local_activity_id ?? '-'),
      'Fila: ' + (error.row_index ?? '-'),
      'Código: ' + (error.codigo ?? '-'),
      'Ruta técnica: ' + (error.property ?? '-'),
      'Mensaje original: ' + (error.descripcion ?? error.message ?? error.motive ?? '-')
    ];

    return lines.join('\n');
  }

  private static validationErrorTitle(error: CueValidationError): string {
    const activityType = String(error.activity_type ?? '').toLowerCase();
    const localId = error.local_activity_id ? ' #' + error.local_activity_id : '';

    if (activityType === 'fito' || activityType === 'tratamfito') {
      return 'Tratamiento fitosanitario' + localId;
    }

    if (activityType === 'ferti' || activityType === 'fertilizacion') {
      return 'Fertilización' + localId;
    }

    return 'Actividad que necesita revisión' + localId;
  }
}
